import React, { useContext } from 'react'

import {
  IsInModalContext,
  ModalActionHeader,
  ModalBackAction,
  ModalCloseAction,
  ModalPaneHeader,
  useModalActionsOverride,
} from '@cash-web/shared-feature-modal'
import { useJourneyScope } from '@cash-web/shared-journey-data-access-context'

export interface BlockerActionHeaderProps {
  onBack?: () => void
  onClose?: () => void
}

/**
 * The actual rendering of the action header is handled by the modal.
 */
const ModalBlockerActionHeader = ({ actionHeaderOverride }: { actionHeaderOverride: React.ReactElement }) => {
  useModalActionsOverride(actionHeaderOverride)
  return null
}

/**
 * Handles the case when a blocker wants to override the behavior of the action header.
 * If the blocker is in a modal, we'll use the useModalActionsOverride hook to override the action header.
 * If the blocker is not in a modal, it will return the default action header that should be rendered by the blocker.
 * @param onBack - The function to call when the back button is clicked.
 * @param onClose - The function to call when the close button is clicked.
 */
export const BlockerActionHeader = ({ onBack, onClose }: BlockerActionHeaderProps) => {
  const isInModal = useContext(IsInModalContext)
  const journeyScope = useJourneyScope()

  if (!onBack && !onClose) {
    return null
  }

  const actionHeader =
    journeyScope.target === 'fullscreen' ? (
      <ModalPaneHeader>{onBack && <ModalBackAction onBack={onBack} />}</ModalPaneHeader>
    ) : (
      <ModalActionHeader>
        {onClose && <ModalCloseAction onClose={onClose} />}
        {onBack && <ModalBackAction onBack={onBack} />}
      </ModalActionHeader>
    )

  if (isInModal) {
    return <ModalBlockerActionHeader actionHeaderOverride={actionHeader} />
  }
  return actionHeader
}
